import { useAuth } from "@/contexts/AuthContext";
import { StatsCard } from "@/components/progress/StatsCard";
import { BadgeGrid } from "@/components/progress/BadgeGrid";
import { QuestPanel } from "@/components/progress/QuestPanel";
import { useProgressData } from "@/hooks/useProgressData";
import { useDailyXP } from "@/hooks/useDailyXP";
import { useAnimationManager } from "@/hooks/useAnimationManager";
import { BadgeEarnedAnimation, QuestCompletedAnimation, LevelUpAnimation } from "@/components/progress/AnimationEffects";
import { useEffect, useRef } from "react";

const Progress = () => {
  const { user } = useAuth();
  const { stats, badges, quests, userQuests, loading, error, refetch } = useProgressData(user?.id);
  const { activeAnimation, completeAnimation, queueBadgeEarned, queueQuestCompleted, queueLevelUp } = useAnimationManager();
  
  // Handle daily XP awarding once per day
  useDailyXP(user?.id);

  // Track previous values to detect changes
  const prevStats = useRef(stats);
  const prevBadges = useRef(badges);
  const prevUserQuests = useRef(userQuests);

  // Detect changes and queue animations
  useEffect(() => {
    if (!user?.id) return;

    // Detect new badges
    if (prevBadges.current.length > 0 && badges.length > prevBadges.current.length) {
      const newBadges = badges.filter(badge => 
        !prevBadges.current.some(prev => prev.badge_slug === badge.badge_slug)
      );
      newBadges.forEach(badge => {
        queueBadgeEarned(badge.badge_slug);
      });
    }

    // Detect level up
    if (prevStats.current.level > 0 && stats.level > prevStats.current.level) {
      queueLevelUp(stats.level);
    }

    // Detect quest completion
    if (prevUserQuests.current.length > 0 && userQuests.length > prevUserQuests.current.length) {
      const newCompletedQuests = userQuests.filter(uq => 
        uq.completed_at && !prevUserQuests.current.some(prev => 
          prev.quest_id === uq.quest_id && prev.completed_at
        )
      );
      newCompletedQuests.forEach(completedQuest => {
        const quest = quests.find(q => q.id === completedQuest.quest_id);
        if (quest) {
          queueQuestCompleted(quest.title, quest.xp);
        }
      });
    }

    // Update previous values
    prevStats.current = stats;
    prevBadges.current = badges;
    prevUserQuests.current = userQuests;
  }, [stats, badges, userQuests, quests, user?.id, queueBadgeEarned, queueLevelUp, queueQuestCompleted]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">⚠️ Something went wrong</div>
          <p className="text-white mb-4">{error}</p>
          <button 
            onClick={refetch}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-center">
          <div className="text-white text-xl mb-4">🔒 Please sign in to view your progress</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold text-white mb-2 animate-pulse">
            ⚡ Player Progress ⚡
          </h1>
          <p className="text-blue-200">Level up your skills and unlock achievements!</p>
        </div>

        {/* Stats Cards */}
        <StatsCard stats={stats} badgeCount={badges.length} />

        {/* Badges Grid */}
        <BadgeGrid badges={badges} />

        {/* Quests Panel */}
        <QuestPanel 
          quests={quests}
          userQuests={userQuests}
          userId={user.id}
          onQuestUpdate={refetch}
        />
      </div>

      {/* Animation Components */}
      {activeAnimation?.type === 'badge_earned' && (
        <BadgeEarnedAnimation
          badgeSlug={activeAnimation.data.badgeSlug}
          isVisible={true}
          onComplete={completeAnimation}
        />
      )}
      
      {activeAnimation?.type === 'quest_completed' && (
        <QuestCompletedAnimation
          questTitle={activeAnimation.data.questTitle}
          xpAwarded={activeAnimation.data.xpAwarded}
          isVisible={true}
          onComplete={completeAnimation}
        />
      )}
      
      {activeAnimation?.type === 'level_up' && (
        <LevelUpAnimation
          newLevel={activeAnimation.data.newLevel}
          isVisible={true}
          onComplete={completeAnimation}
        />
      )}
    </div>
  );
};

export default Progress;